import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, CircularProgress } from '@mui/joy';
import { Backdrop } from '@mui/material';
import { Detail as DetailComponent, InfoHeader } from 'src/mtska-frontend-app-component';
import { useDataProvider } from 'src/mtska-frontend-data-provider';
import { useResolvedMediaSrc } from 'src/mtska-frontend-data-provider/hooks/useResolvedMediaSrc';
import { AppContext } from 'src/mtska-frontend-app-scaffolding';

const clipDataProviderName = 'propertymanagement/propertyclip';
const clipModel = 'propertymanagement/propertyclip';
const wizardAction = 'generateClip';

const GalleryThumb = ({ url, selected, disabled, onToggle }) => {
    const { src, ready, loading } = useResolvedMediaSrc(url);

    return (
        <Box
            className={`property-clip-thumb${selected ? ' selected' : ''}${disabled ? ' disabled' : ''}`}
            onClick={() => !disabled && onToggle()}
            sx={{
                position: 'relative',
                width: 120,
                height: 90,
                borderRadius: 8,
                overflow: 'hidden',
                border: selected ? '2px solid var(--joy-palette-primary-500)' : '1px solid var(--joy-palette-neutral-300)',
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
            }}
        >
            {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <CircularProgress size="sm" />
                </Box>
            )}
            {ready && src && (
                <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
            <Checkbox
                checked={selected}
                disabled={disabled}
                onChange={onToggle}
                sx={{ position: 'absolute', top: 4, left: 4, bgcolor: 'rgba(255,255,255,0.85)' }}
            />
        </Box>
    );
};

const GenerateClip = (params) => {
    const { t } = useTranslation();
    const { existingModules, userCapabilities } = useContext(AppContext);
    const property = params.property ?? params.detailStack?.property ?? params.relatedElements?.property;

    const [selectedIndexes, setSelectedIndexes] = useState([]);
    const [localError, setLocalError] = useState();
    const [wizardValues, setWizardValues] = useState({ prompt: '' });

    const { doAction, loadOne, errorMessage, errors, saving, success } = useDataProvider(clipDataProviderName, params.multipart);
    const { generateClipConfig } = existingModules[clipDataProviderName] ?? {};

    const gallery = useMemo(() => {
        return Array.isArray(property?.gallery) ? property.gallery : [];
    }, [property?.gallery]);

    useEffect(() => {
        loadOne(null);
    }, []);

    useEffect(() => {
        if (success) {
            params.handleClose?.(params.uid, { propertyId: property?.id });
        }
    }, [success]);

    const toggleImage = (index) => {
        setSelectedIndexes((current) => {
            if (current.includes(index)) {
                return current.filter((item) => item !== index);
            }
            if (current.length >= 3) {
                return current;
            }
            return [...current, index].sort((a, b) => a - b);
        });
    };

    const onProcessStepAction = (currentStep, stepTotal, actionInvoked, getValues) => {
        setLocalError();

        if (actionInvoked < 0) {
            return true;
        }

        const values = getValues();
        const prompt = values.prompt?.trim();

        if (!prompt) {
            setLocalError(t('Enter a prompt for the clip'));
            return false;
        }

        if (!property?.id) {
            setLocalError(t('Property not found'));
            return false;
        }

        if (selectedIndexes.length < 1) {
            setLocalError(t('Select between 1 and 3 gallery images'));
            return false;
        }

        return doAction({
            action: wizardAction,
            model: clipModel,
            data: {
                property: { id: property.id },
                prompt,
                sourceImageIndexes: selectedIndexes,
            },
        });
    };

    const onValidateStepAction = (currentStep, getValues, setValue, setError, resultValue) => {
        return resultValue?.type === 'ACTION_SUCCESS';
    };

    if (!property?.id) {
        return <Box sx={{ p: 2 }}>{t('Property not found')}</Box>;
    }

    return (
        <>
            <DetailComponent
                disableDetailConfigOverride={true}
                values={wizardValues}
                modelName="propertyclip"
                detailConfig={generateClipConfig}
                dataProviderName={clipDataProviderName}
                saving={saving}
                errors={errors}
                errorMessage={errorMessage}
                alertMessage={localError}
                title={t('Generate clip')}
                canSave={userCapabilities && userCapabilities[clipDataProviderName + '.generateclip']}
                header={
                    <InfoHeader
                        view="property"
                        item={property}
                        title={t('Generate clip')}
                        subtitle={t('Select up to 3 gallery images and describe the clip to generate with Veo.')}
                    />
                }
                onProcessStepAction={onProcessStepAction}
                onValidateStepAction={onValidateStepAction}
                {...params}
            />
            <Box className="property-clip-gallery-picker" sx={{ px: 2, pb: 2, mt: -2 }}>
                <Box sx={{ mb: 1, fontWeight: 600 }}>{t('Source images')} ({selectedIndexes.length}/3)</Box>
                {gallery.length === 0 ? (
                    <Box>{t('This property has no gallery images. Import images before generating a clip.')}</Box>
                ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {gallery.map((item, index) => (
                            <GalleryThumb
                                key={index}
                                url={item?.url}
                                selected={selectedIndexes.includes(index)}
                                disabled={!selectedIndexes.includes(index) && selectedIndexes.length >= 3}
                                onToggle={() => toggleImage(index)}
                            />
                        ))}
                    </Box>
                )}
            </Box>
            {saving && (
                <Backdrop open sx={{ backgroundColor: 'rgba(255,255,255,0.6)' }}>
                    <CircularProgress />
                </Backdrop>
            )}
        </>
    );
};

export default GenerateClip;
